// English source strings for M2: people and hosts, visits, visitor policies and approvals.
export const enM2 = {
  // ------------------------------------------------------------ API messages
  'api.visitClosed': 'This visit is cancelled or has ended, so it can no longer be changed.',
  'api.scheduleInvalid': 'The visit window needs attention.',
  'api.submissionIncomplete': 'The visit cannot be submitted yet. Complete the required details.',
  'api.roomConflict': 'This room is already booked for part of this time. Choose another time or room.',
  'api.capacityExceeded': 'This destination holds {capacity} people; the visit has {count}.',
  'api.visitImpact': 'This change affects existing approvals. Review the impact and confirm to re-request approval.',
  'api.lastParticipant': 'A submitted visit needs at least one visitor. Cancel the visit instead.',
  'api.hostInactive': 'Your host profile is inactive. Ask your administrator to reactivate it.',
  'api.hostHasVisits': 'This host has future visits. Reassign them to another host first.',
  'api.ownVisitApproval': 'You host or created this visit, so another reviewer must decide it.',
  'api.approvalNotAssigned': 'This approval is assigned to someone else.',
  'api.approvalNotPending': 'This approval is no longer waiting for a decision.',
  'api.approvalStale': 'The visit changed after you opened it. Review the current details before deciding.',
  'api.invitationInvalid': 'This invitation link is not valid. It may have expired, been replaced or the visit may have changed. Contact your host for a new invitation.',
  'api.invitationNotCleared': 'An invitation can be sent only after the visit is approved for this person.',
  'v.noEmail': 'This visitor has no email address. Create a link to share instead.',
  'api.policyDraftExists': 'A draft already exists for this scope. Publish or discard it first.',

  // ------------------------------------------------------------ validation
  'v.date': 'Use a date in the form YYYY-MM-DD.',
  'v.phone': 'Enter a phone number using digits, spaces, brackets or hyphens.',
  'v.policyKey': 'Use 1–40 lowercase letters, digits, hyphens or underscores, starting with a letter.',
  'v.policyDuplicate': 'This code is already used.',
  'v.policyUnknownDependency': 'Depends on a stage that does not exist here.',
  'v.policySelfDependency': 'A stage cannot depend on itself.',
  'v.policyCycle': 'These stages depend on each other in a loop.',
  'v.policyNoActiveCategory': 'Keep at least one category active.',
  'v.policyApproverUnknown': 'Choose an active person as the named approver.',
  'v.timeGap': 'This local time does not exist on that date because of a daylight-saving change. Choose another time.',
  'v.timeAmbiguous': 'This local time happens twice on that date because of a daylight-saving change. Choose which one you mean.',
  'v.endBeforeStart': 'The end must be after the start.',
  'v.visitTooLong': 'Visits of this category can last at most {days} days.',
  'v.visitInPast': 'This visit window has already ended.',
  'v.destinationUnavailable': 'Choose an active location at this site.',
  'v.hostUnavailable': 'Choose active hosts.',
  'v.categoryUnknown': 'Choose an active visitor category.',
  'v.personUnknown': 'This profile is not available.',
  'v.personDuplicate': 'This person is already on the visit.',
  'v.noParticipants': 'Add at least one visitor.',
  'v.recurrenceNone': 'No dates match this repeat pattern.',
  'v.recurrenceTooMany': 'A series can have at most {max} occurrences.',
  'v.noteUnknown': 'The note being corrected is not on this visit.',
  'v.rejectReason': 'Give a reason for rejecting.',
  'v.approverConflict': 'This person hosts or created the visit and cannot decide it.',
  'v.approverNoGrant': 'This person cannot approve visits at this site.',
  'v.userUnknown': 'Choose an active user.',
  'v.hostUserLinked': 'This user is already linked to another host profile.',
  'v.hostEmployeeTaken': 'Another host already uses this employee number.',

  // ------------------------------------------------------------ independent state labels
  'visit.planning.draft': 'Draft',
  'visit.planning.submitted': 'Submitted',
  'visit.planning.cancelled': 'Cancelled',
  'visit.planning.expired': 'Ended',
  'state.approval.not_required': 'No approval needed',
  'state.approval.pending': 'Approval pending',
  'state.approval.approved': 'Approved',
  'state.approval.rejected': 'Rejected',
  'state.approval.expired': 'Approval expired',
  'state.approval.invalidated': 'Approval withdrawn',
  'state.evidence.missing': 'No evidence yet',
  'state.evidence.uploaded': 'Evidence uploaded',
  'state.evidence.scanning': 'Evidence being checked',
  'state.evidence.review_pending': 'Evidence review pending',
  'state.evidence.accepted': 'Evidence accepted',
  'state.evidence.rejected': 'Evidence rejected',
  'state.evidence.expired': 'Evidence expired',
  'state.evidence.unavailable': 'Evidence unavailable',
  'state.presence.no_observation': 'Not observed',
  'state.presence.observed_onsite': 'Observed onsite',
  'state.presence.observed_departed': 'Observed departed',
  'state.presence.unknown_conflicting': 'Presence unknown',
  'state.credential.none': 'No credential',
  'state.credential.requested': 'Credential requested',
  'state.credential.issued_active': 'Credential active',
  'state.credential.revoke_pending': 'Revocation pending',
  'state.credential.revoked': 'Credential revoked',
  'state.credential.expired': 'Credential expired',
  'state.credential.failed': 'Credential failed',
  'state.credential.unknown': 'Credential outcome unknown',
  'task.status.waiting': 'Waiting for earlier stage',
  'task.status.pending': 'Awaiting decision',
  'task.status.blocked': 'Blocked — no eligible reviewer',
  'task.status.approved': 'Approved',
  'task.status.rejected': 'Rejected',
  'task.status.invalidated': 'Withdrawn',
  'task.status.closed': 'Closed',
  'task.closed.material_change': 'Withdrawn after a material change',
  'task.closed.participant_removed': 'Withdrawn: visitor removed',
  'task.closed.visit_cancelled': 'Withdrawn: visit cancelled',
  'task.closed.rejected_elsewhere': 'Closed: another stage rejected',

  // ------------------------------------------------------------ visits list
  'visits.title': 'Visits & invitations',
  'visits.intro': 'Plan individual, group and recurring visits. Each participant keeps an independent lifecycle.',
  'visits.new': '+ New visit',
  'visits.tab.upcoming': 'Upcoming',
  'visits.tab.drafts': 'Drafts',
  'visits.tab.past': 'Past',
  'visits.tab.cancelled': 'Cancelled',
  'visits.scope.mine': 'Visits I host',
  'visits.scope.site': 'Whole site',
  'visits.scopeLabel': 'Show',
  'visits.search': 'Search title or visitor name',
  'visits.register': 'Visit register',
  'visits.count': { one: '{count} record in scope', other: '{count} records in scope' },
  'visits.col.visit': 'Visit',
  'visits.col.when': 'When ({zone})',
  'visits.col.host': 'Host',
  'visits.col.destination': 'Destination',
  'visits.col.people': 'People',
  'visits.col.approval': 'Approval',
  'visits.col.planning': 'Planning',
  'visits.empty': 'No visits in this view.',
  'visits.noSchedule': 'Not scheduled',
  'visits.untitled': 'Untitled draft',
  'visits.approvalSummary': '{approved} approved · {pending} pending · {rejected} rejected',
  'visits.noApproval': 'No approval needed',
  'visits.daily': 'Daily {start}–{end}',
  'visits.series': 'Recurring',

  // ------------------------------------------------------------ visit form
  'vform.newTitle': 'Plan a visit',
  'vform.editTitle': 'Edit visit',
  'vform.intro': 'Save a draft at any time. Submitting checks the policy for this site and starts approvals.',
  'vform.details': 'Visit details',
  'vform.title': 'Title',
  'vform.category': 'Visitor category',
  'vform.categoryHint': 'Categories, required details and approvals come from the published policy (version {version}).',
  'vform.categoryStages': { zero: '{label} — no approval', one: '{label} — 1 approval stage', other: '{label} — {count} approval stages' },
  'vform.choose': 'Choose…',
  'vform.purpose': 'Purpose',
  'vform.destination': 'Destination',
  'vform.destinationNone': 'No specific destination',
  'vform.capacity': '{name} (capacity {capacity})',
  'vform.restricted': 'restricted',
  'vform.schedule': 'Visit window',
  'vform.zoneNote': 'Times are local to {site}: {zone} ({offset}).',
  'vform.mode': 'Schedule type',
  'vform.mode.continuous': 'One continuous window',
  'vform.mode.daily': 'Several days, daily hours only',
  'vform.modeDailyHint': 'Access is limited to the daily hours on each day — never overnight.',
  'vform.startDate': 'Start date',
  'vform.startTime': 'Start time',
  'vform.endDate': 'End date',
  'vform.endTime': 'End time',
  'vform.dailyStart': 'Daily opening',
  'vform.dailyEnd': 'Daily closing',
  'vform.ambiguous': 'This time occurs twice because clocks change that day.',
  'vform.ambiguous.earlier': 'The first occurrence (before the change)',
  'vform.ambiguous.later': 'The second occurrence (after the change)',
  'vform.repeat': 'Repeat weekly',
  'vform.repeatHint': 'Each occurrence becomes its own visit with its own approvals; changing one never changes the others.',
  'vform.weekdays': 'On',
  'vform.until': 'Until',
  'vform.hosts': 'Hosts',
  'vform.primaryHost': 'Primary host',
  'vform.primaryHostSelf': '{name} (you)',
  'vform.primaryHostHint': 'The primary host remains accountable for the visit.',
  'vform.additionalHosts': 'Additional hosts',
  'vform.additionalHostsHint': 'Additional hosts share responsibility and notifications. They do not gain approval rights.',
  'vform.addHost': 'Add host',
  'vform.removeHost': 'Remove {name}',
  'vform.visitors': 'Visitors',
  'vform.visitorsHint': 'Each visitor keeps their own approval, identity, presence and credential status.',
  'vform.visitor': 'Visitor {n}',
  'vform.givenName': 'Given name',
  'vform.familyName': 'Family name',
  'vform.email': 'Email',
  'vform.phone': 'Mobile',
  'vform.organisation': 'Organisation',
  'vform.required': 'required by policy',
  'vform.addVisitor': 'Add visitor',
  'vform.removeVisitor': 'Remove visitor {n}',
  'vform.existingProfile': 'Using existing profile: {name}',
  'vform.useNew': 'Enter a new person instead',
  'vform.matches': 'Possible existing profiles',
  'vform.matchesNote': 'Shared contact details are not proof of identity. Use a profile only if it is the same person.',
  'vform.matchReason': 'Matched on {reasons}',
  'vform.reason.email': 'email',
  'vform.reason.phone': 'mobile',
  'vform.reason.name': 'name',
  'vform.useProfile': 'Use this profile',
  'vform.keepSeparate': 'Keep separate',
  'vform.saveDraft': 'Save draft',
  'vform.submit': 'Submit for approval',
  'vform.saveChanges': 'Save changes',
  'vform.cancel': 'Back',
  'vform.seriesResult': 'Series created',
  'vform.seriesOutcome': '{date}: {outcome}',
  'vform.outcome.draft': 'draft saved',
  'vform.outcome.submitted': 'submitted',
  'vform.outcome.failed': 'not submitted — {message}',
  'vform.impactTitle': 'Re-request approval?',
  'vform.impactBody': { zero: 'Changing {fields} is a material change. Approval will be requested again under the current policy.', one: 'Changing {fields} is a material change. 1 existing approval decision will be withdrawn and approval requested again.', other: 'Changing {fields} is a material change. {count} existing approval decisions will be withdrawn and approval requested again.' },
  'vform.impactConfirm': 'Save and re-request approval',
  'vform.conflictTitle': 'Someone else changed this visit',
  'vform.conflictBody': 'Your edits were not saved. Compare with the latest version, then reload and apply your changes again.',
  'vform.conflictYours': 'Your value',
  'vform.conflictCurrent': 'Current value',
  'vform.reload': 'Reload latest version',
  'vform.field.categoryCode': 'category',
  'vform.field.destinationId': 'destination',
  'vform.field.startsAt': 'start',
  'vform.field.endsAt': 'end',
  'vform.field.scheduleMode': 'schedule type',
  'vform.field.dailyStart': 'daily opening',
  'vform.field.dailyEnd': 'daily closing',
  'vform.field.primaryHostId': 'primary host',
  'weekday.1': 'Mon',
  'weekday.2': 'Tue',
  'weekday.3': 'Wed',
  'weekday.4': 'Thu',
  'weekday.5': 'Fri',
  'weekday.6': 'Sat',
  'weekday.7': 'Sun',

  // ------------------------------------------------------------ visit detail
  'visit.unavailable': 'Visit unavailable',
  'visit.unavailableBody': 'This visit does not exist or is outside your view.',
  'visit.back': 'Return to visits',
  'visit.copy': 'Copy',
  'visit.edit': 'Edit',
  'visit.cancel': 'Cancel visit',
  'visit.submit': 'Submit for approval',
  'visit.journey': 'Visit journey',
  'visit.journeyNote': 'Facts remain independent; a sent invitation or an approval does not imply admission.',
  'visit.step.plan': 'Planning',
  'visit.step.approval': 'Approval',
  'visit.step.identity': 'Identity',
  'visit.step.presence': 'Presence',
  'visit.step.credential': 'Credential',
  'visit.details': 'Visit details',
  'visit.detailsSub': 'One authoritative record',
  'visit.f.host': 'Primary host',
  'visit.f.additionalHosts': 'Additional hosts',
  'visit.f.destination': 'Destination',
  'visit.f.window': 'Visit window',
  'visit.f.windows': { one: '{count} authorised window', other: '{count} authorised windows' },
  'visit.f.category': 'Category',
  'visit.f.purpose': 'Purpose',
  'visit.f.site': 'Site',
  'visit.f.policy': 'Policy version',
  'visit.f.policyValue': 'Version {number}',
  'visit.f.version': 'Record version',
  'visit.f.copiedFrom': 'Copied from',
  'visit.f.occurrence': 'Series occurrence',
  'visit.people': 'People',
  'visit.peopleSub': 'Each person has an independent lifecycle.',
  'visit.col.person': 'Person',
  'visit.col.approval': 'Approval',
  'visit.col.evidence': 'Identity',
  'visit.col.presence': 'Presence',
  'visit.col.credential': 'Credential',
  'visit.removed': 'Removed',
  'visit.removeVisitor': 'Remove',
  'visit.removeTitle': 'Remove {name} from this visit?',
  'visit.removeBody': 'Their approval tasks are withdrawn. Other visitors are not affected.',
  'visit.addVisitors': 'Add visitors',
  'visit.masked': 'Contact details are masked for your role.',
  'visit.approvals': 'Approvals',
  'visit.approvalsSub': 'Created from policy version {version}; decisions are never rewritten.',
  'visit.approvalsNone': 'No approval tasks.',
  'visit.anyApprover': 'Any eligible site approver',
  'visit.decidedBy': '{name} · {when}',
  'visit.next': 'Next action',
  'visit.nextDraft': 'Complete these details to submit:',
  'visit.nextReady': 'Ready to submit.',
  'visit.nextPending': 'Waiting for approval decisions.',
  'visit.nextApproved': 'All required approvals are recorded. Identity, arrival and access remain separate checks.',
  'visit.nextCancelled': 'Cancelled: {reason}. Invitations are invalidated. Presence and access facts are retained.',
  'visit.notes': 'Notes',
  'visit.notesSub': 'Time-stamped and append-only. Corrections reference the original.',
  'visit.notesNone': 'No notes yet.',
  'visit.noteAdd': 'Add note',
  'visit.noteBody': 'Note',
  'visit.noteCorrect': 'Correct',
  'visit.noteCorrecting': 'Correcting the note from {when}',
  'visit.noteCorrection': 'Correction of an earlier note',
  'visit.noteCorrected': 'Corrected later',
  'visit.cancelTitle': 'Cancel this visit?',
  'visit.cancelBody': { one: 'Approvals for {count} visitor are withdrawn and invitations invalidated. Cancelling never records anyone as departed.', other: 'Approvals for {count} visitors are withdrawn and invitations invalidated. Cancelling never records anyone as departed.' },
  'visit.cancelConfirm': 'Cancel visit',
  'visit.copied': 'Copied into a new draft. Approvals and credentials are not copied.',

  // ------------------------------------------------------------ approvals
  'approvals.title': 'Approval inbox',
  'approvals.intro': 'Review the current facts. Changing material visit details requires a new decision.',
  'approvals.policies': 'Workflow policies',
  'approvals.m.queue': 'Your site queue',
  'approvals.m.queueSub': 'Decisions assigned to you or any site approver',
  'approvals.m.blocked': 'Blocked',
  'approvals.m.blockedSub': 'No eligible reviewer — never skipped',
  'approvals.m.mode': 'Decision mode',
  'approvals.m.modeValue': 'Human',
  'approvals.m.modeSub': 'No automated approval',
  'approvals.tab.pending': 'Awaiting a decision',
  'approvals.tab.decided': 'Decided by you',
  'approvals.tab.blocked': 'Blocked',
  'approvals.col.visitor': 'Visitor',
  'approvals.col.visit': 'Visit',
  'approvals.col.when': 'When',
  'approvals.col.stage': 'Stage',
  'approvals.col.action': 'Action',
  'approvals.empty': 'Nothing is waiting here.',
  'approvals.review': 'Review decision',
  'approvals.ownVisit': 'You host this visit — another reviewer decides',
  'approvals.notAssigned': 'Assigned to someone else',
  'approvals.restricted': 'Restricted area',
  'approvals.decisionTitle': 'Approval decision',
  'approvals.decisionSub': '{stage} · policy version {version}',
  'approvals.facts': 'Facts you are deciding on (visit version {version})',
  'approvals.decision': 'Decision',
  'approvals.approve': 'Approve',
  'approvals.reject': 'Reject',
  'approvals.reason': 'Reason',
  'approvals.reasonHint': 'Required when rejecting. Recorded with the decision.',
  'approvals.record': 'Record decision',
  'approvals.recorded': 'Decision recorded.',
  'approvals.reassign': 'Reassign',
  'approvals.reassignTitle': 'Reassign approval',
  'approvals.reassignTo': 'Assign to',
  'approvals.reassignAny': 'Any eligible site approver',
  'approvals.reassignUser': 'A named person',
  'approvals.reassignUserId': 'Person',
  'approvals.reassignHint': 'The visit’s host, co-hosts and creator cannot be chosen.',
  'approvals.reassignSave': 'Reassign',
  'approvals.decidedOn': 'Decided {when}',

  // ------------------------------------------------------------ visitor policies
  'vpolicy.title': 'Policies & approval workflows',
  'vpolicy.intro': 'Versioned visitor categories, required details and approval stages. Published versions never change; every decision records the version that produced it.',
  'vpolicy.scope': 'Policy scope',
  'vpolicy.scope.org': 'Organisation (all sites)',
  'vpolicy.scope.site': '{name} (site override)',
  'vpolicy.inForce': 'In force',
  'vpolicy.inForceSub': 'Version {number} · published {when}',
  'vpolicy.inheritsOrg': 'This site has no override; the organisation policy (version {number}) applies.',
  'vpolicy.none': 'No published policy.',
  'vpolicy.col.category': 'Category',
  'vpolicy.col.required': 'Required details',
  'vpolicy.col.maxDays': 'Longest visit',
  'vpolicy.col.stages': 'Approval sequence',
  'vpolicy.days': { one: '{count} day', other: '{count} days' },
  'vpolicy.noStages': 'No approval',
  'vpolicy.restrictedStages': 'Added for restricted destinations',
  'vpolicy.inactive': 'inactive',
  'vpolicy.req.purpose': 'Purpose',
  'vpolicy.req.destination': 'Destination',
  'vpolicy.req.visitor.email': 'Visitor email',
  'vpolicy.req.visitor.phone': 'Visitor mobile',
  'vpolicy.req.visitor.organisation': 'Visitor organisation',
  'vpolicy.draft': 'Draft',
  'vpolicy.draftSub': 'Version {number} · not in force until published',
  'vpolicy.startDraft': 'Start a draft from the version in force',
  'vpolicy.startOverride': 'Create a site override',
  'vpolicy.categories': 'Visitor categories',
  'vpolicy.addCategory': 'Add category',
  'vpolicy.removeCategory': 'Remove category',
  'vpolicy.code': 'Code',
  'vpolicy.labelEn': 'Label (English)',
  'vpolicy.labelAr': 'Label (Arabic)',
  'vpolicy.active': 'Active',
  'vpolicy.maxDays': 'Longest visit (days)',
  'vpolicy.required': 'Required details',
  'vpolicy.stages': 'Approval stages',
  'vpolicy.stagesHint': 'Stages without a predecessor run in parallel. Every stage must approve.',
  'vpolicy.addStage': 'Add stage',
  'vpolicy.removeStage': 'Remove stage',
  'vpolicy.stageKey': 'Stage code',
  'vpolicy.resolver': 'Who decides',
  'vpolicy.resolver.site_approvers': 'Any eligible site approver',
  'vpolicy.resolver.user': 'A named person',
  'vpolicy.resolverUser': 'Named approver (user id)',
  'vpolicy.afterStage': '{stage} (after {after})',
  'vpolicy.dependsOn': 'After',
  'vpolicy.maxOccurrences': 'Most occurrences in one recurring series',
  'vpolicy.note': 'Change note',
  'vpolicy.saveDraft': 'Save draft',
  'vpolicy.saved': 'Draft saved.',
  'vpolicy.publish': 'Publish',
  'vpolicy.publishTitle': 'Publish version {number}?',
  'vpolicy.publishBody': 'New submissions and re-evaluations use this version. Existing decisions keep the version that produced them.',
  'vpolicy.published': 'Version {number} is now in force.',
  'vpolicy.discard': 'Discard draft',
  'vpolicy.history': 'Version history',
  'vpolicy.col.version': 'Version',
  'vpolicy.col.status': 'Status',
  'vpolicy.col.published': 'Published',
  'vpolicy.col.note': 'Note',
  'vpolicy.restore': 'Restore as draft',
  'vpolicy.restoreHint': 'Restoring creates a new version; history is never deleted.',
  'vpolicy.readOnly': 'You can read this policy. Changes are made by organisation administrators.',
  'vpolicy.status.draft': 'Draft',
  'vpolicy.status.published': 'In force',
  'vpolicy.status.superseded': 'Superseded',
  'vpolicy.status.discarded': 'Discarded',
  'policy.status.draft': 'draft',
  'policy.status.published': 'published',
  'policy.status.superseded': 'superseded',
  'policy.status.discarded': 'discarded',

  // ------------------------------------------------------------ hosts and people
  'hosts.tab': 'Host directory',
  'hosts.title': 'Host directory',
  'hosts.sub': 'Hosts are separate from sign-in accounts. Users with the host experience get a profile automatically.',
  'hosts.add': 'Add host',
  'hosts.col.host': 'Host',
  'hosts.col.department': 'Department',
  'hosts.col.employee': 'Employee number',
  'hosts.col.account': 'Sign-in account',
  'hosts.col.status': 'Status',
  'hosts.linked': 'Linked',
  'hosts.notLinked': 'None',
  'hosts.source.self': 'from account',
  'hosts.source.manual': 'added manually',
  'hosts.source.directory': 'from directory',
  'hosts.source.import': 'imported',
  'hosts.displayName': 'Name',
  'hosts.email': 'Email',
  'hosts.employeeNumber': 'Employee number',
  'hosts.department': 'Department',
  'hosts.jobTitle': 'Job title',
  'hosts.deactivate': 'Deactivate',
  'hosts.reactivate': 'Reactivate',
  'hosts.impactTitle': 'Reassign future visits first',
  'hosts.empty': 'No hosts yet.',
  'person.title': 'Visitor profile',
  'person.history': 'Visit history',
  'person.partial': 'Some visits are at sites outside your view and are not shown.',
  'person.none': 'No visits in your view.',
  'person.organisation': 'Organisation',
  'person.contact': 'Contact',

  // ------------------------------------------------------------ visitor pass (public) and invitations
  'pass.title': 'You’re expected at {site}',
  'pass.greeting': 'Welcome, {name}.',
  'pass.intro': '{host} has invited you. Show this pass at reception.',
  'pass.when': 'When',
  'pass.daily': 'Daily {start}–{end}',
  'pass.where': 'Where',
  'pass.host': 'Host',
  'pass.reference': 'Reference',
  'pass.referenceHint': 'If you cannot use the QR code, quote this reference at reception.',
  'pass.qr': 'QR code for visit reference {reference}',
  'pass.calendar': 'Add to calendar',
  'pass.directions': 'Directions and instructions',
  'pass.address': 'Address',
  'pass.attendance': 'Will you attend?',
  'pass.confirm': 'I will attend',
  'pass.decline': 'I cannot attend',
  'pass.rsvp.confirmed': 'Thank you — your reply is recorded and visible to your host.',
  'pass.rsvp.declined': 'Thank you — your reply is recorded and visible to your host.',
  'pass.notEntry': 'This pass does not by itself grant entry. Reception confirms your arrival on site.',
  'pass.validUntil': 'This link works until {date}.',
  'pass.invalid.title': 'This invitation link is not valid',
  'pass.invalid.body': 'It may have expired or been replaced, or the visit may have changed. Contact your host for a new invitation.',
  'pass.loading': 'Opening your visitor pass…',
  'inv.col': 'Invitation',
  'inv.none': 'Not sent',
  'inv.waiting': 'Sent after approval',
  'inv.noEmail': 'No email — share a link',
  'inv.active': 'Link active',
  'inv.viewed': 'Opened {when}',
  'inv.superseded': 'Replaced by a newer link',
  'inv.revoked': 'Link withdrawn',
  'inv.msg.queued': 'Email queued',
  'inv.msg.accepted': 'Email accepted by mail service',
  'inv.msg.failed': 'Email not accepted — retrying',
  'inv.msg.blocked': 'Email blocked: {reason}',
  'inv.msg.cancellation': 'Cancellation notice',
  'inv.rsvp.none': 'No reply',
  'inv.rsvp.confirmed': 'Will attend',
  'inv.rsvp.declined': 'Cannot attend',
  'inv.reissue': 'Invitation…',
  'inv.reissueTitle': 'New invitation for {name}',
  'inv.reissueBody': 'The current link stops working as soon as you continue.',
  'inv.mode.email': 'Send a new invitation email',
  'inv.mode.link': 'Create a link to share myself',
  'inv.reissueGo': 'Continue',
  'inv.linkReady': 'Share this link with {name}. It is shown only once and works until {date}.',
  'inv.emailQueued': 'A new invitation email is on its way.',
  'inv.acceptedNote': '“Accepted” means the mail service took the message — not that it was read.',
} as const;
